import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { combineLatest, filter, firstValueFrom, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { MembershipTierService } from '../api-services/membership-tier.service';
import { MembershipTier } from '../data-models/membership-tier.model';
import { resolveMembershipTierAccess } from './membership-tier-guard-logic';

/**
 * Route data shape for `MembershipTierGuard`.
 *
 * ```ts
 * { path: 'analyses', data: { requiredTier: 'pro', preview: '/dashboard' } }
 * ```
 *
 * `preview` is a temporary redirect target until Task Group 10 ships the
 * real Core-facing preview pages — see the routes' own comments in
 * `app-routing.module.ts` for which stand-in each currently points at.
 */
export interface MembershipTierRouteData {
  requiredTier: MembershipTier;
  preview: string;
}

/**
 * Verifies the signed-in user's ORGANIZATION (not the user themselves —
 * tier is org-wide, per spec.md's locked decision #1) is on the required
 * tier. Modeled on `MembershipRoleGuard`'s auth-readiness wait
 * (`combineLatest([authService.loading$, authService.currentUser$])` +
 * `take(1)`), reading `route.data['requiredTier']`/`['preview']` instead of
 * `membershipRoles`.
 *
 * Reads `MembershipTierService.isPro()` after awaiting `awaitReady()` —
 * `AuthService` fires `fetchStatus()` without awaiting it (PR #106, to keep
 * it off the login critical path), so `isPro()` can still hold its
 * pre-fetch default on a fast initial/deep-link navigation. Awaiting the
 * SAME in-flight fetch here (only when the route actually needs tier data)
 * closes that race without re-triggering a redundant Hub call or slowing
 * down ungated routes.
 *
 * A thin wrapper — the actual allow/redirect decision is
 * `resolveMembershipTierAccess` (membership-tier-guard-logic.ts), a pure
 * function tested directly since this repo's Jest config can't parse a
 * bare `@angular/router` import (see that file's own docstring).
 */
@Injectable({ providedIn: 'root' })
export class MembershipTierGuard {
  constructor(
    private authService: AuthService,
    private membershipTierService: MembershipTierService,
    private router: Router,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const requiredTier = route.data['requiredTier'] as MembershipTier | undefined;
    const preview = route.data['preview'] as string | undefined;

    const [, user] = await firstValueFrom(
      combineLatest([this.authService.loading$, this.authService.currentUser$]).pipe(
        filter(([loading]) => !loading),
        take(1),
      ),
    );

    if (requiredTier) {
      await this.membershipTierService.awaitReady();
    }

    const decision = resolveMembershipTierAccess({
      isAuthenticated: !!user,
      requiredTier,
      isPro: this.membershipTierService.isPro(),
      preview,
    });
    return decision.allow ? true : this.router.createUrlTree([decision.redirectTo]);
  }
}
